<template>
  <q-dialog
    :model-value="modelValue"
    persistent
    @update:model-value="$emit('update:modelValue', $event)"
  >
    <q-card class="align-dlg" style="border-radius: 14px;">
      <q-card-section class="row items-center q-py-sm q-px-md align-dlg__head">
        <q-icon name="rule" color="deep-orange-7" size="22px" class="q-mr-sm" />
        <div class="align-dlg__title">Выравнивание факта по смете</div>
        <q-space />
        <q-btn icon="close" flat round dense v-close-popup :disable="busy || applying" />
      </q-card-section>

      <div v-if="subtitle" class="align-dlg__subtitle">
        <q-icon name="more_horiz" size="14px" class="q-mr-xs text-grey-6" />
        <span class="align-dlg__subtitle-text">{{ subtitle }}</span>
        <q-tooltip v-if="subtitle.length > 80" max-width="520px" anchor="top middle" self="bottom middle">
          {{ subtitle }}
        </q-tooltip>
      </div>

      <q-separator />

      <q-card-section class="q-pt-md q-pb-sm">
        <div v-if="busy && !summary" class="row items-center q-py-md" style="color:#607d8b;font-size:13px">
          <q-spinner color="deep-orange-7" size="20px" class="q-mr-sm" />
          Считаем, что на что поменяется…
        </div>

        <div v-else-if="summary">
          <div class="row q-col-gutter-sm q-mb-md">
            <div class="col-3">
              <div class="stat-box" style="background:#eceff1">
                <div class="stat-lbl">Всего связок</div>
                <div class="stat-val">{{ totalCombos }}</div>
              </div>
            </div>
            <div class="col-3">
              <div class="stat-box" style="background:#e8f5e9">
                <div class="stat-lbl">Будет изменено</div>
                <div class="stat-val" style="color:#2e7d32">{{ alignedCount }}</div>
              </div>
            </div>
            <div class="col-3">
              <div class="stat-box" :style="skippedCount > 0 ? 'background:#fff3e0' : 'background:#eceff1'">
                <div class="stat-lbl">Пропустится</div>
                <div class="stat-val" :style="skippedCount > 0 ? 'color:#ef6c00' : 'color:#78909c'">
                  {{ skippedCount }}
                </div>
              </div>
            </div>
            <div class="col-3">
              <div class="stat-box" :style="orphansCount > 0 ? 'background:#ffebee' : 'background:#eceff1'">
                <div class="stat-lbl">Фантомы удалим</div>
                <div class="stat-val" :style="orphansCount > 0 ? 'color:#c62828' : 'color:#78909c'">
                  {{ orphansCount }}
                </div>
              </div>
            </div>
          </div>

          <!-- Алерт про фантомные записи -->
          <div v-if="orphansCount > 0" class="align-orphans">
            <q-icon name="delete_forever" size="18px" color="red-7" class="q-mr-sm" />
            <div class="align-orphans__body">
              <div class="align-orphans__title">
                Лишние записи факта без сметы — будут удалены ({{ orphansCount }} шт.)
              </div>
              <div class="align-orphans__text">
                По этой работе в факте остались строки, для которых в смете больше нет соответствующих работ.
                Такие записи <b>«висят в воздухе»</b> и искусственно завышают сумму факта.
                Мы их удалим, чтобы факт снова совпадал со сметой.
              </div>
              <div class="align-orphans__sum">
                Лишняя сумма в факте: <b>−{{ fmtMoney(orphansSumOrigin) }} ₸</b>
              </div>
              <div class="align-orphans__dev">
                <a href="#" class="align-orphans__toggle" @click.prevent="showOrphanDev = !showOrphanDev">
                  <q-icon :name="showOrphanDev ? 'expand_less' : 'expand_more'" size="14px" class="q-mr-xs" />
                  {{ showOrphanDev ? 'Скрыть технические детали' : 'Технические детали (для разработчика)' }}
                </a>
                <div v-if="showOrphanDev" class="align-orphans__devbox">
                  Это строки <code>wcheck.usp_fact_tab</code> с теми же
                  <code>project_id / floor_map_id / usp_work_guid</code>, что и у выравниваемых работ,
                  но их <b>(work_set_id, work_set_check_group_id)</b> отсутствует в
                  <code>wcheck.remont_work_set_tab</code> для этого проекта и этажа.
                  Обычно это следы старой функции <code>align_to_smeta (v1)</code>
                  (<code>fact_value = 1</code>, <code>work_set_price is null</code>, цена проставлена руками).
                  Выровнять их нельзя — сметы для них нет. Функция <code>align_to_smeta_v2</code>
                  удаляет такие строки и их материалы (<code>usp_fact_material_tab</code>) в рамках этой же транзакции.
                </div>
              </div>
            </div>
          </div>

          <!-- Предупреждение, если backend ещё не возвращает "было" -->
          <div v-if="!hasBeforeData" class="align-warn">
            <q-icon name="warning" size="16px" color="orange-8" class="q-mr-xs" />
            <div>
              Версия SQL-функции в БД пока не возвращает текущие значения факта
              (<code>before</code>). Показано только то, что получится после применения.
              Чтобы видеть &laquo;было → станет&raquo;, накатите обновлённый
              <code>CREATE_wcheck_usp_fact__align_to_smeta_v2.sql</code>.
            </div>
          </div>

          <!-- Сводка дельты по всем связкам -->
          <div v-if="delta && hasBeforeData" class="align-delta">
            <div class="align-delta__head">
              <q-icon name="compare_arrows" size="16px" color="blue-grey-7" class="q-mr-xs" />
              <span>Суммарно по выбранным работам:</span>
            </div>
            <div class="row q-col-gutter-sm">
              <div class="col-4">
                <div class="align-delta__row">
                  <div class="align-delta__lbl">Объём</div>
                  <div class="align-delta__vals">
                    <span class="before">{{ fmtNum(delta.before.value) }}</span>
                    <q-icon name="arrow_right_alt" size="14px" class="q-mx-xs text-grey-6" />
                    <span class="after">{{ fmtNum(delta.after.value) }}</span>
                    <span class="diff" :class="diffClass(delta.diff.value)">
                      {{ diffSign(delta.diff.value) }}{{ fmtNum(Math.abs(delta.diff.value)) }}
                    </span>
                  </div>
                </div>
              </div>
              <div class="col-4">
                <div class="align-delta__row">
                  <div class="align-delta__lbl">СМР (работа + ТМЦ подряд)</div>
                  <div class="align-delta__vals">
                    <span class="before">{{ fmtMoney(delta.before.priceO) }}</span>
                    <q-icon name="arrow_right_alt" size="14px" class="q-mx-xs text-grey-6" />
                    <span class="after">{{ fmtMoney(delta.after.priceO) }}</span>
                    <span class="diff" :class="diffClass(delta.diff.priceO)">
                      {{ diffSign(delta.diff.priceO) }}{{ fmtMoney(Math.abs(delta.diff.priceO)) }}
                    </span>
                  </div>
                  <div class="align-delta__sub" v-if="hasMatBreakdown">
                    <div>работа: {{ fmtMoney(delta.before.workO) }} → {{ fmtMoney(delta.after.workO) }}</div>
                    <div>ТМЦ подряд: {{ fmtMoney(delta.before.matContrO) }} → {{ fmtMoney(delta.after.matContrO) }}</div>
                  </div>
                </div>
              </div>
              <div class="col-4">
                <div class="align-delta__row">
                  <div class="align-delta__lbl">ТМЦ команда проекта</div>
                  <div class="align-delta__vals">
                    <span v-if="!hasMatData" class="text-grey-6" style="font-size:11.5px">
                      нет данных
                      <q-tooltip>
                        SQL-функция в БД ещё не возвращает <code>mat_price_origin</code>.
                        Накатите обновлённый CREATE_wcheck_usp_fact__align_to_smeta_v2.sql.
                      </q-tooltip>
                    </span>
                    <template v-else>
                      <span class="before">{{ fmtMoney(delta.before.matO) }}</span>
                      <q-icon name="arrow_right_alt" size="14px" class="q-mx-xs text-grey-6" />
                      <span class="after">{{ fmtMoney(delta.after.matO) }}</span>
                      <span class="diff" :class="diffClass(delta.diff.matO)">
                        {{ diffSign(delta.diff.matO) }}{{ fmtMoney(Math.abs(delta.diff.matO)) }}
                      </span>
                    </template>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Подсказка -->
          <div class="align-hint">
            <div class="align-hint__head">
              <q-icon name="help_outline" size="16px" color="blue-grey-6" class="q-mr-xs" />
              <span>Что означают карточки:</span>
            </div>
            <ul class="align-hint__list">
              <li>
                <b style="color:#455a64">Всего связок</b> — количество комбинаций
                (проект + этаж + работа + группа), которые относятся к выбранной работе УСП.
              </li>
              <li>
                <b style="color:#2e7d32">Будет изменено</b> — связки, по которым уже есть
                записи оперфакта. Для них факт пересчитается из сметы (объём, цена, материалы).
              </li>
              <li>
                <b style="color:#ef6c00">Пропустится</b> — связки, по которым в факте ещё нет ни одной записи.
                В режиме <b style="color:#2e7d32">Безопасно</b> они не трогаются.
                В режиме <b style="color:#c62828">Принудительно</b> для них будет создан
                факт с нуля на основании данных сметы.
              </li>
              <li>
                <b style="color:#c62828">Фантомы удалим</b> — записи факта, для которых
                в смете больше нет соответствующей работы (например, работу убрали из сметы,
                а факт остался). Такие записи завышают итоговую сумму.
                <template v-if="orphansCount > 0">
                  Сейчас найдено <b>{{ orphansCount }}</b> — будут удалены в обоих режимах.
                </template>
                <template v-else>
                  Сейчас таких нет — значит всё чисто.
                </template>
              </li>
            </ul>

            <div class="align-hint__head q-mt-sm">
              <q-icon name="tune" size="16px" color="blue-grey-6" class="q-mr-xs" />
              <span>Режимы применения:</span>
            </div>
            <ul class="align-hint__list">
              <li>
                <b style="color:#2e7d32">Выровнять ({{ alignedCount }})</b> — пересчитает только те связки,
                где факт уже введён. Пустые связки останутся без изменений.
              </li>
              <li>
                <b style="color:#c62828">Принудительно ({{ totalCombos }})</b> — пересчитает все связки.
                Где факта не было — создаст новые записи из сметы.
              </li>
            </ul>
            <div class="align-hint__note">
              Сейчас вы видите предварительный расчёт — в базу ничего не записано.
              Изменения применятся только после нажатия кнопки.
            </div>
          </div>

          <!-- Подробный diff по связкам -->
          <q-expansion-item
            v-if="planRows.length"
            :default-opened="planRows.length <= 20"
            header-class="text-caption text-grey-8 q-pa-xs"
            expand-separator
            dense
            class="q-mt-sm"
          >
            <template #header>
              <q-item-section>
                <div class="text-caption text-grey-8">
                  Построчно: что на что поменяется ({{ planRows.length }})
                </div>
              </q-item-section>
            </template>

            <div class="align-plan">
              <table class="align-plan__table">
                <thead v-if="hasBeforeData">
                  <tr>
                    <th rowspan="2">Проект</th>
                    <th rowspan="2">Этаж</th>
                    <th rowspan="2">
                      Связка
                      <q-icon name="help_outline" size="13px" class="q-ml-xs text-grey-5" />
                      <q-tooltip>work_set_id / work_set_check_group_id</q-tooltip>
                    </th>
                    <th colspan="3" class="c">Объём</th>
                    <th colspan="3" class="c">СМР (работа + ТМЦ подряд)</th>
                    <th v-if="hasMatData" colspan="3" class="c">ТМЦ команда проекта</th>
                  </tr>
                  <tr>
                    <th class="r sub">было</th>
                    <th class="r sub">станет</th>
                    <th class="r sub">Δ</th>
                    <th class="r sub">было</th>
                    <th class="r sub">станет</th>
                    <th class="r sub">Δ</th>
                    <template v-if="hasMatData">
                      <th class="r sub">было</th>
                      <th class="r sub">станет</th>
                      <th class="r sub">Δ</th>
                    </template>
                  </tr>
                </thead>
                <thead v-else>
                  <tr>
                    <th>Проект</th>
                    <th>Этаж</th>
                    <th>
                      Связка
                      <q-icon name="help_outline" size="13px" class="q-ml-xs text-grey-5" />
                      <q-tooltip>work_set_id / work_set_check_group_id</q-tooltip>
                    </th>
                    <th class="r">Объём (станет)</th>
                    <th class="r">Цена СМР (станет)</th>
                  </tr>
                </thead>
                <tbody>
                  <tr
                    v-for="(p, idx) in planSample"
                    :key="idx"
                    :class="{
                      'align-plan__skip': p.action === 'skip',
                      'align-plan__orphan': p.action === 'delete_orphan'
                    }"
                  >
                    <td>{{ p.project_id }}</td>
                    <td>{{ p.floor_map_id }}</td>
                    <td>
                      <q-icon
                        v-if="p.action === 'skip'"
                        name="block"
                        size="14px"
                        color="orange-7"
                        class="q-mr-xs"
                      >
                        <q-tooltip>Будет пропущена — нет реального оперфакта</q-tooltip>
                      </q-icon>
                      <q-icon
                        v-else-if="p.action === 'delete_orphan'"
                        name="delete_forever"
                        size="14px"
                        color="red-7"
                        class="q-mr-xs"
                      >
                        <q-tooltip>Будет удалена — нет в смете (фантом)</q-tooltip>
                      </q-icon>
                      {{ p.work_set_id }}/{{ p.work_set_check_group_id }}
                    </td>

                    <template v-if="hasBeforeData">
                      <td class="r">{{ fmtNum(beforeOf(p).fact_value) }}</td>
                      <td class="r">
                        <span v-if="p.action === 'skip'" class="text-grey-6">—</span>
                        <span v-else-if="p.action === 'delete_orphan'" class="text-red-8">удалится</span>
                        <b v-else>{{ fmtNum(afterOf(p).fact_value) }}</b>
                      </td>
                      <td class="r" :class="diffClass(rowDiff(p, 'fact_value'))">
                        <span v-if="p.action === 'skip'">—</span>
                        <template v-else-if="rowDiff(p, 'fact_value') === 0">0</template>
                        <template v-else>
                          {{ diffSign(rowDiff(p, 'fact_value')) }}{{ fmtNum(Math.abs(rowDiff(p, 'fact_value'))) }}
                        </template>
                      </td>
                      <td class="r">
                        {{ fmtMoney(beforeOf(p).fact_price_origin) }}
                        <div v-if="hasMatBreakdown" class="cell-sub">
                          <div><span class="cell-sub__lbl">раб</span> {{ fmtMoney(beforeOf(p).work_price_origin) }}</div>
                          <div><span class="cell-sub__lbl">подр</span> {{ fmtMoney(beforeOf(p).mat_contr_price_origin) }}</div>
                        </div>
                      </td>
                      <td class="r">
                        <span v-if="p.action === 'skip'" class="text-grey-6">—</span>
                        <span v-else-if="p.action === 'delete_orphan'" class="text-red-8">удалится</span>
                        <template v-else>
                          <b>{{ fmtMoney(afterOf(p).fact_price_origin) }}</b>
                          <div v-if="hasMatBreakdown" class="cell-sub">
                            <div><span class="cell-sub__lbl">раб</span> {{ fmtMoney(afterOf(p).work_price_origin) }}</div>
                            <div><span class="cell-sub__lbl">подр</span> {{ fmtMoney(afterOf(p).mat_contr_price_origin) }}</div>
                          </div>
                        </template>
                      </td>
                      <td class="r" :class="diffClass(rowDiff(p, 'fact_price_origin'))">
                        <span v-if="p.action === 'skip'">—</span>
                        <template v-else-if="rowDiff(p, 'fact_price_origin') === 0">0,00</template>
                        <template v-else>
                          {{ diffSign(rowDiff(p, 'fact_price_origin')) }}{{ fmtMoney(Math.abs(rowDiff(p, 'fact_price_origin'))) }}
                        </template>
                      </td>

                      <template v-if="hasMatData">
                        <td class="r">{{ fmtMoney(beforeOf(p).mat_price_origin) }}</td>
                        <td class="r">
                          <span v-if="p.action === 'skip'" class="text-grey-6">—</span>
                          <span v-else-if="p.action === 'delete_orphan'" class="text-red-8">удалится</span>
                          <b v-else>{{ fmtMoney(afterOf(p).mat_price_origin) }}</b>
                        </td>
                        <td class="r" :class="diffClass(rowDiff(p, 'mat_price_origin'))">
                          <span v-if="p.action === 'skip'">—</span>
                          <template v-else-if="rowDiff(p, 'mat_price_origin') === 0">0,00</template>
                          <template v-else>
                            {{ diffSign(rowDiff(p, 'mat_price_origin')) }}{{ fmtMoney(Math.abs(rowDiff(p, 'mat_price_origin'))) }}
                          </template>
                        </td>
                      </template>
                    </template>

                    <template v-else>
                      <td class="r">
                        <span v-if="p.action === 'skip'" class="text-grey-6">—</span>
                        <span v-else-if="p.action === 'delete_orphan'" class="text-red-8">удалится</span>
                        <b v-else>{{ fmtNum(afterOnly(p, 'fact_value')) }}</b>
                      </td>
                      <td class="r">
                        <span v-if="p.action === 'skip'" class="text-grey-6">—</span>
                        <span v-else-if="p.action === 'delete_orphan'" class="text-red-8">удалится</span>
                        <b v-else>{{ fmtMoney(afterOnly(p, 'fact_price_origin')) }}</b>
                      </td>
                    </template>
                  </tr>
                </tbody>
              </table>
            </div>
            <div
              v-if="planRows.length > planSample.length"
              class="align-plan__more"
            >
              Показаны первые {{ planSample.length }} из {{ planRows.length }} записей.
            </div>
          </q-expansion-item>
        </div>

        <div v-else-if="error" class="q-py-md" style="color:#c62828; font-size:13px">
          <q-icon name="error" size="18px" class="q-mr-xs" />
          {{ error }}
        </div>
      </q-card-section>

      <q-separator />

      <!-- Принудительный режим — отдельная зона, далеко от основных кнопок -->
      <div v-if="summary && !busy" class="align-force-zone">
        <a
          v-if="!showForce"
          href="#"
          class="align-force-toggle"
          @click.prevent="showForce = true"
        >
          <q-icon name="expand_more" size="13px" class="q-mr-xs" />
          Принудительный режим (крайний случай)
        </a>
        <div v-else class="align-force-open">
          <q-icon name="warning" size="14px" color="red-7" class="q-mr-xs" />
          <span class="align-force-warn">Создаст факт даже там, где его не было — используй осознанно</span>
          <q-btn
            :label="forceLabel"
            unelevated
            no-caps
            dense
            size="sm"
            color="red-7"
            class="q-ml-sm"
            :loading="applying && lastApplied === 'force'"
            :disable="applying || totalCombos === 0"
            @click="onApply('force')"
          />
          <a href="#" class="align-force-cancel" @click.prevent="showForce = false">отмена</a>
        </div>
      </div>

      <q-separator />

      <q-card-actions class="q-px-md q-py-sm align-dlg__actions">
        <q-btn
          label="Отмена"
          flat
          no-caps
          color="grey-7"
          :disable="applying"
          v-close-popup
        />
        <q-space />
        <q-btn
          :label="safeLabel"
          unelevated
          no-caps
          color="green-7"
          :loading="applying && lastApplied === 'safe'"
          :disable="!summary || busy || applying || alignedCount === 0"
          @click="onApply('safe')"
        >
          <q-tooltip>Только связки с реальным оперфактом</q-tooltip>
        </q-btn>
      </q-card-actions>
    </q-card>
  </q-dialog>
</template>

<script>
export default {
  name: "PpmAlignToSmetaDialog",
  props: {
    modelValue: Boolean,
    subtitle: { type: String, default: "" },
    summary: { type: Object, default: null },
    busy: Boolean,
    applying: Boolean,
    error: { type: String, default: "" },
  },
  emits: ["update:modelValue", "apply"],
  data() {
    return {
      lastApplied: null,
      showOrphanDev: false,
      showForce: false,
    };
  },
  computed: {
    totalCombos() {
      if (!this.summary) return 0;
      return (this.summary.combos_aligned || 0) + (this.summary.combos_skipped || 0);
    },
    skippedCount() {
      return this.summary?.combos_skipped || 0;
    },
    alignedCount() {
      return this.summary?.combos_aligned || 0;
    },
    orphansCount() {
      return this.summary?.combos_orphaned || 0;
    },
    orphansSumOrigin() {
      return this.planRows
        .filter(p => p.action === "delete_orphan")
        .reduce((s, p) => s + Number((p.before || {}).fact_price_origin || 0), 0);
    },
    safeLabel() {
      return `Выровнять (${this.alignedCount})`;
    },
    forceLabel() {
      return `Принудительно (${this.totalCombos})`;
    },
    planRows() {
      const plan = this.summary?.plan || [];
      // Порядок: orphans → align → skip (важное сверху).
      const weight = (a) => a === "delete_orphan" ? 0 : a === "align" ? 1 : 2;
      return [...plan].sort((a, b) => weight(a.action) - weight(b.action));
    },
    planSample() {
      return this.planRows.slice(0, 100);
    },
    hasBeforeData() {
      return this.planRows.some(p => p && p.before && typeof p.before === "object");
    },
    hasMatData() {
      return this.planRows.some(p =>
        (p.before && p.before.mat_price_origin != null) ||
        (p.after  && p.after.mat_price_origin  != null)
      );
    },
    hasMatBreakdown() {
      return this.planRows.some(p =>
        (p.before && (p.before.work_price_origin != null || p.before.mat_contr_price_origin != null)) ||
        (p.after  && (p.after.work_price_origin  != null || p.after.mat_contr_price_origin  != null))
      );
    },
    delta() {
      if (!this.planRows.length || !this.hasBeforeData) return null;
      const acc = {
        before: { value: 0, price: 0, priceO: 0, mat: 0, matO: 0, work: 0, workO: 0, matContr: 0, matContrO: 0 },
        after:  { value: 0, price: 0, priceO: 0, mat: 0, matO: 0, work: 0, workO: 0, matContr: 0, matContrO: 0 },
      };
      const addInto = (target, src) => {
        target.value     += Number(src.fact_value || 0);
        target.price     += Number(src.fact_price || 0);
        target.priceO    += Number(src.fact_price_origin || 0);
        target.mat       += Number(src.mat_price || 0);
        target.matO      += Number(src.mat_price_origin || 0);
        target.work      += Number(src.work_price || 0);
        target.workO     += Number(src.work_price_origin || 0);
        target.matContr  += Number(src.mat_contr_price || 0);
        target.matContrO += Number(src.mat_contr_price_origin || 0);
      };
      for (const p of this.planRows) {
        const b = p.before || {};
        addInto(acc.before, b);
        if (p.action === "align" || p.action === "delete_orphan") {
          addInto(acc.after, p.after || {});
        } else {
          addInto(acc.after, b);
        }
      }
      return {
        before: acc.before,
        after: acc.after,
        diff: {
          value:    acc.after.value    - acc.before.value,
          price:    acc.after.price    - acc.before.price,
          priceO:   acc.after.priceO   - acc.before.priceO,
          mat:      acc.after.mat      - acc.before.mat,
          matO:     acc.after.matO     - acc.before.matO,
          work:     acc.after.work     - acc.before.work,
          workO:    acc.after.workO    - acc.before.workO,
          matContr: acc.after.matContr - acc.before.matContr,
          matContrO:acc.after.matContrO- acc.before.matContrO,
        },
      };
    },
  },
  watch: {
    applying(val) {
      if (!val) this.lastApplied = null;
    },
    modelValue(val) {
      if (!val) this.showForce = false;
    },
  },
  methods: {
    onApply(mode) {
      this.lastApplied = mode;
      this.$emit("apply", { force: mode === "force" });
    },
    beforeOf(p) {
      return p.before || {};
    },
    afterOf(p) {
      return p.after || {};
    },
    afterOnly(p, field) {
      // Поддержка старого формата плана без before/after — значения лежат прямо в корне.
      if (p.after && p.after[field] != null) return p.after[field];
      return p[field];
    },
    rowDiff(p, field) {
      if (p.action === "skip") return 0;
      const b = Number((p.before || {})[field] || 0);
      const a = Number((p.after || {})[field] || 0);
      return a - b;
    },
    diffSign(n) {
      if (!n) return "";
      return n > 0 ? "+" : "−";
    },
    diffClass(n) {
      if (!n) return "diff-zero";
      return n > 0 ? "diff-up" : "diff-down";
    },
    fmtNum(v) {
      if (v == null || v === "") return "—";
      const n = Number(v);
      if (!Number.isFinite(n)) return "—";
      return new Intl.NumberFormat("ru-RU", {
        minimumFractionDigits: 0,
        maximumFractionDigits: 2,
      }).format(n);
    },
    fmtMoney(v) {
      if (v == null || v === "") return "—";
      const n = Number(v);
      if (!Number.isFinite(n)) return "—";
      return new Intl.NumberFormat("ru-RU", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }).format(n);
    },
  },
};
</script>

<style scoped>
.align-dlg {
  min-width: 720px;
  width: 860px;
  max-width: 95vw;
}
.align-dlg__head {
  background: #fafafa;
}
.align-dlg__title {
  font-size: 15px;
  font-weight: 700;
  color: #2c3e50;
}
.align-dlg__subtitle {
  padding: 6px 16px 10px;
  background: #fafafa;
  color: #607d8b;
  font-size: 12px;
  display: flex;
  align-items: center;
  border-top: 1px solid #eceff1;
}
.align-dlg__subtitle-text {
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
  display: inline-block;
  max-width: 100%;
  cursor: default;
}

.stat-box {
  padding: 10px 12px;
  border-radius: 8px;
}
.stat-lbl {
  font-size: 11px;
  color: #607d8b;
  text-transform: uppercase;
  letter-spacing: 0.4px;
}
.stat-val {
  font-size: 20px;
  font-weight: 700;
  color: #37474f;
  margin-top: 2px;
  line-height: 1;
}

.align-warn {
  display: flex;
  align-items: flex-start;
  gap: 4px;
  border: 1px solid #ffe0b2;
  background: #fff8e1;
  color: #6d4c41;
  border-radius: 8px;
  padding: 8px 12px;
  font-size: 12.5px;
  line-height: 1.5;
  margin-bottom: 10px;
}
.align-warn code {
  background: rgba(0, 0, 0, 0.05);
  padding: 0 4px;
  border-radius: 3px;
  font-size: 11.5px;
}

.align-delta {
  border: 1px solid #eceff1;
  background: #fafbfc;
  border-radius: 8px;
  padding: 10px 12px;
  margin-bottom: 10px;
}
.align-delta__head {
  display: flex;
  align-items: center;
  font-size: 12px;
  color: #546e7a;
  margin-bottom: 8px;
  font-weight: 600;
}
.align-delta__row {
  background: #fff;
  border: 1px solid #eceff1;
  border-radius: 6px;
  padding: 6px 10px;
}
.align-delta__lbl {
  font-size: 10.5px;
  color: #90a4ae;
  text-transform: uppercase;
  letter-spacing: 0.4px;
  margin-bottom: 2px;
}
.align-delta__vals {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  font-size: 13px;
  font-variant-numeric: tabular-nums;
}
.align-delta__sub {
  margin-top: 3px;
  font-size: 10.5px;
  color: #78909c;
  font-variant-numeric: tabular-nums;
}
.align-plan__table .cell-sub {
  font-size: 10.5px;
  color: #78909c;
  margin-top: 4px;
  font-weight: 400;
  line-height: 1.35;
  border-top: 1px dashed #eceff1;
  padding-top: 3px;
}
.align-plan__table .cell-sub__lbl {
  display: inline-block;
  width: 32px;
  color: #b0bec5;
  text-transform: uppercase;
  font-size: 9.5px;
  letter-spacing: 0.3px;
}
.align-delta__vals .before {
  color: #90a4ae;
  text-decoration: line-through;
  text-decoration-color: #cfd8dc;
}
.align-delta__vals .after {
  color: #2c3e50;
  font-weight: 700;
}
.align-delta__vals .diff {
  margin-left: 8px;
  font-size: 11.5px;
  font-weight: 700;
  padding: 1px 6px;
  border-radius: 999px;
}

.diff-up {
  color: #2e7d32;
  background: #e8f5e9;
}
.diff-down {
  color: #c62828;
  background: #ffebee;
}
.diff-zero {
  color: #90a4ae;
}

.align-hint {
  border: 1px solid #e3f2fd;
  background: #f5faff;
  border-radius: 8px;
  padding: 10px 12px;
  font-size: 12.5px;
  color: #37474f;
}
.align-hint__head {
  display: flex;
  align-items: center;
  font-weight: 600;
  margin-bottom: 6px;
  color: #455a64;
}
.align-hint__list {
  margin: 0 0 6px 0;
  padding-left: 20px;
  line-height: 1.55;
}
.align-hint__list li {
  margin-bottom: 3px;
}
.align-hint__note {
  margin-top: 4px;
  padding-top: 6px;
  border-top: 1px dashed #cfd8dc;
  font-size: 11.5px;
  color: #78909c;
}

.align-plan {
  max-height: 320px;
  overflow: auto;
  border: 1px solid #eceff1;
  border-radius: 6px;
  margin-top: 6px;
}
.align-plan__table {
  width: 100%;
  border-collapse: collapse;
  font-size: 12px;
  font-variant-numeric: tabular-nums;
}
.align-plan__table thead {
  background: #f5f5f5;
  position: sticky;
  top: 0;
  z-index: 1;
}
.align-plan__table th {
  padding: 6px 8px;
  text-align: left;
  color: #455a64;
  font-weight: 600;
  border-bottom: 1px solid #eceff1;
}
.align-plan__table th.sub {
  font-weight: 500;
  font-size: 10.5px;
  color: #78909c;
  text-transform: uppercase;
  letter-spacing: 0.3px;
}
.align-plan__table th.c {
  text-align: center;
  background: #f0f4f8;
  border-left: 1px solid #e0e0e0;
}
.align-plan__table th.r,
.align-plan__table td.r {
  text-align: right;
}
.align-plan__table td {
  padding: 4px 8px;
  border-top: 1px solid #f0f0f0;
}
.align-plan__skip {
  background: #fff8f1;
}
.align-plan__orphan {
  background: #ffebee;
}
.align-plan__orphan td {
  color: #b71c1c;
}

.align-orphans {
  display: flex;
  align-items: flex-start;
  border: 1px solid #ffcdd2;
  background: #fff5f5;
  border-radius: 8px;
  padding: 10px 12px;
  margin-bottom: 10px;
}
.align-orphans__body {
  flex: 1;
  font-size: 12.5px;
  line-height: 1.5;
  color: #4e342e;
}
.align-orphans__title {
  font-weight: 700;
  color: #c62828;
  margin-bottom: 4px;
}
.align-orphans__text {
  color: #5d4037;
  margin-bottom: 6px;
}
.align-orphans__text code {
  background: rgba(0, 0, 0, 0.06);
  padding: 0 4px;
  border-radius: 3px;
  font-size: 11.5px;
}
.align-orphans__sum {
  font-size: 12.5px;
  font-variant-numeric: tabular-nums;
  color: #c62828;
}
.align-orphans__dev {
  margin-top: 6px;
  padding-top: 6px;
  border-top: 1px dashed #ffcdd2;
}
.align-orphans__toggle {
  display: inline-flex;
  align-items: center;
  font-size: 11.5px;
  color: #8d6e63;
  text-decoration: none;
  cursor: pointer;
}
.align-orphans__toggle:hover {
  color: #4e342e;
  text-decoration: underline;
}
.align-orphans__devbox {
  margin-top: 6px;
  padding: 8px 10px;
  background: #fff;
  border: 1px solid #ffe0e0;
  border-radius: 6px;
  font-size: 11.5px;
  line-height: 1.5;
  color: #5d4037;
}
.align-orphans__devbox code {
  background: rgba(0, 0, 0, 0.06);
  padding: 0 4px;
  border-radius: 3px;
  font-size: 11px;
}
.align-plan__more {
  font-size: 11px;
  color: #78909c;
  margin-top: 4px;
  padding: 0 4px;
}

.align-dlg__actions {
  background: #fafafa;
}

.align-force-zone {
  padding: 6px 16px 8px;
  background: #fafafa;
  border-top: 1px solid #eceff1;
}
.align-force-toggle {
  font-size: 11.5px;
  color: #b0bec5;
  text-decoration: none;
  display: inline-flex;
  align-items: center;
}
.align-force-toggle:hover {
  color: #e53935;
  text-decoration: underline;
}
.align-force-open {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 6px;
  font-size: 12px;
}
.align-force-warn {
  color: #c62828;
  font-size: 12px;
}
.align-force-cancel {
  font-size: 11.5px;
  color: #90a4ae;
  text-decoration: none;
  margin-left: 4px;
}
.align-force-cancel:hover {
  text-decoration: underline;
}
</style>
